import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLinkedinDeal, saveLinkedinDeal, saveLinkedinDealReset, deleteLinkedinDeal, deleteLinkedinDealReset } from "../../state/linkedinPromotion/linkedinPromotionSlice";
import { selectLinkedinPromotionState } from "../../state/linkedinPromotion/linkedinPromotionSelector";

const INTEGRATION_TYPE = {
  MANAGED_BY_ATS: "MANAGED_BY_ATS",
  MANAGED_BY_COMPANY: "MANAGED_BY_COMPANY",
};

const DEFAULT_VALUES = {
  totalSlots: 0,
  expireDate: "",
  monthlyCreditAmount: 0,
  linkedInCompanyId: "",
  linkedInCompanyName: "",
};

const LinkedinSlotsModal = ({ onClose, companyInfo }) => {
  const dispatch = useDispatch();
  const { deal, isLoading, isSaving, isSaveSuccess, isDeleting, isDeleteSuccess } = useSelector(selectLinkedinPromotionState);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [integrationType, setIntegrationType] = useState(INTEGRATION_TYPE.MANAGED_BY_ATS);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_VALUES });

  // Fetch deal on mount
  useEffect(() => {
    if (companyInfo?.id) {
      dispatch(getLinkedinDeal(companyInfo.id));
    }
  }, [companyInfo?.id, dispatch]);

  // Populate form when deal is loaded
  useEffect(() => {
    if (deal) {
      const type = deal.integrationType || INTEGRATION_TYPE.MANAGED_BY_ATS;
      setIntegrationType(type);
      reset({
        totalSlots: deal.totalSlots,
        expireDate: deal.expireDate && deal.expireDate !== "9999-01-01T00:00:00.000Z" ? deal.expireDate.split("T")[0] : "",
        monthlyCreditAmount: deal.monthlyCreditAmount || 0,
        linkedInCompanyId: deal.linkedInCompanyId || "",
        linkedInCompanyName: deal.linkedInCompanyName || "",
      });
    }
  }, [deal, reset]);

  // Close modal on save success
  useEffect(() => {
    if (isSaveSuccess) {
      dispatch(saveLinkedinDealReset());
      onClose();
    }
  }, [isSaveSuccess, dispatch, onClose]);

  // Reset form + close on delete success
  useEffect(() => {
    if (isDeleteSuccess) {
      dispatch(deleteLinkedinDealReset());
      reset(DEFAULT_VALUES);
      onClose();
    }
  }, [isDeleteSuccess, dispatch, reset, onClose]);

  const handleIntegrationTypeChange = (e) => {
    const newType = e.target.value;
    setIntegrationType(newType);
    // Clear ATS-only fields when switching to company-managed
    if (newType === INTEGRATION_TYPE.MANAGED_BY_COMPANY) {
      reset((prev) => ({
        totalSlots: prev.totalSlots,
        expireDate: "",
        monthlyCreditAmount: 0,
        linkedInCompanyId: "",
        linkedInCompanyName: "",
      }));
    }
  };

  const onSubmit = (data) => {
    const payload = {
      companyId: companyInfo.id,
      totalSlots: data.totalSlots,
      integrationType,
    };

    if (integrationType === INTEGRATION_TYPE.MANAGED_BY_ATS) {
      payload.expireDate = data.expireDate;
      payload.monthlyCreditAmount = data.monthlyCreditAmount;
      payload.linkedInCompanyId = data.linkedInCompanyId;
      payload.linkedInCompanyName = data.linkedInCompanyName;
    }

    if (deal?.id) payload.id = deal.id;
    dispatch(saveLinkedinDeal(payload));
  };

  const handleResetClick = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    dispatch(deleteLinkedinDeal(deal.id));
  };

  const handleCopyFeedUrl = () => {
    if (deal?.feedUrl) {
      navigator.clipboard.writeText(deal.feedUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const isCompanyManaged = integrationType === INTEGRATION_TYPE.MANAGED_BY_COMPANY;

  if (isLoading && !deal) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {/* Integration Type Selector */}
        <FormControl>
          <FormLabel sx={{ mb: 1, fontWeight: 600 }}>LinkedIn Job Distribution Method</FormLabel>
          <RadioGroup value={integrationType} onChange={handleIntegrationTypeChange}>
            <FormControlLabel
              value={INTEGRATION_TYPE.MANAGED_BY_ATS}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2" fontWeight={500}>
                  BT1 LinkedIn Talent Solutions
                </Typography>
              }
              disabled={!!deal?.id} // lock type on existing deals
            />
            <FormControlLabel
              value={INTEGRATION_TYPE.MANAGED_BY_COMPANY}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2" fontWeight={500}>
                  Company's Own LinkedIn Talent Solutions
                </Typography>
              }
              disabled={!!deal?.id} // lock type on existing deals
            />
          </RadioGroup>
        </FormControl>

        {/* Number of Slots — shared by both types */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1 }}>
            Number of Slots
            {isCompanyManaged && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (informational)
              </Typography>
            )}
          </FormLabel>
          <TextField
            size="small"
            type="number"
            {...register("totalSlots", {
              valueAsNumber: true,
              required: "Number of slots is required",
              min: { value: 1, message: "Must be at least 1" },
            })}
            error={!!errors.totalSlots}
            helperText={errors.totalSlots?.message}
          />
        </FormControl>

        {/* ATS-only fields */}
        {!isCompanyManaged && (
          <>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Contract Expiry Date</FormLabel>
              <TextField
                size="small"
                type="date"
                {...register("expireDate", { required: "Expiry date is required" })}
                InputLabelProps={{ shrink: true }}
                error={!!errors.expireDate}
                helperText={errors.expireDate?.message}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>Monthly Discount Deal Amount for All Slots</FormLabel>
              <TextField
                size="small"
                type="number"
                {...register("monthlyCreditAmount", {
                  valueAsNumber: true,
                  required: "Monthly discount deal amount is required",
                  min: { value: 0, message: "Must be >= 0" },
                })}
                error={!!errors.monthlyCreditAmount}
                helperText={errors.monthlyCreditAmount?.message}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>LinkedIn Company ID</FormLabel>
              <TextField
                size="small"
                {...register("linkedInCompanyId", { required: "LinkedIn Company ID is required" })}
                error={!!errors.linkedInCompanyId}
                helperText={errors.linkedInCompanyId?.message}
              />
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1 }}>LinkedIn Company Name</FormLabel>
              <TextField
                size="small"
                {...register("linkedInCompanyName", { required: "LinkedIn Company Name is required" })}
                error={!!errors.linkedInCompanyName}
                helperText={errors.linkedInCompanyName?.message}
              />
            </FormControl>
          </>
        )}

        {/* Company-managed: Feed URL (shown after deal is saved) */}
        {isCompanyManaged && deal?.feedUrl && (
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1 }}>XML Feed URL</FormLabel>
            <TextField
              size="small"
              value={deal.feedUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied!" : "Copy URL"}>
                      <IconButton onClick={handleCopyFeedUrl} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ "& input": { color: "text.secondary", fontFamily: "monospace", fontSize: 12 } }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Share this URL with LinkedIn to integrate with your company's own Talent Solutions account.
            </Typography>
          </FormControl>
        )}

        {/* Company-managed: Feed URL placeholder when deal not yet saved */}
        {isCompanyManaged && !deal?.feedUrl && (
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
            <Typography variant="caption" color="text.secondary">
              💡 After saving, an XML Feed URL will be generated here for you to share with LinkedIn.
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {deal?.id && (
            <Button type="button" variant="outlined" color="error" fullWidth disabled={isDeleting || isSaving} onClick={handleResetClick}>
              {isDeleting ? "Removing..." : "Remove Promotion Deal"}
            </Button>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={isSaving || isDeleting}>
            {isSaving ? "Saving..." : deal?.id ? "Update & Save" : "Confirm & Assign"}
          </Button>
        </Stack>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleConfirmClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove LinkedIn Deal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently <strong>delete</strong> the LinkedIn deal for <strong>{companyInfo?.name}</strong>
            {!isCompanyManaged && " and also remove all active job promotions linked to it"}. This action <strong>cannot be undone</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleConfirmClose} variant="outlined" fullWidth>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" fullWidth>
            Yes, Delete &amp; Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkedinSlotsModal;
